import React, { useState } from 'react';
import CloudUploadOutlined from '@mui/icons-material/CloudUploadOutlined';
import api from '../../../services/api';
import { Alert, Button, Card, Input } from '../../../components/ui';

const PasInventoryUpload = ({ onImported }) => {
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [diagnostics, setDiagnostics] = useState([]);
    const [result, setResult] = useState(null);
    const [importingFile, setImportingFile] = useState('');

    const upload = async () => {
        if (!file) {
            setError('Choose a CSV inventory file.');
            return;
        }

        setBusy(true);
        setError('');
        setDiagnostics([]);
        setResult(null);
        setImportingFile(file.name);
        try {
            const form = new FormData();
            form.append('inventory', file);
            const response = await api.post('/api/topology/import-assets', form);
            setResult(response.data);
            await onImported?.();
        } catch (uploadError) {
            setError(uploadError.response?.data?.message || 'PAS inventory import failed.');
            setDiagnostics(uploadError.response?.data?.summary?.diagnostics || []);
        } finally {
            setBusy(false);
            setImportingFile('');
        }
    };

    const importedAssets = Array.isArray(result?.assets) ? result.assets : [];

    return (
        <Card className="mt-5">
            <div className="space-y-4 p-5">
                <div>
                    <h3 className="font-semibold text-foreground">Import PAS asset inventory</h3>
                    <p className="mt-1 text-sm text-muted-fg">
                        Upload the asset inventory CSV. Only FM030 Concourse D and E records are imported;
                        other projects and empty rows are ignored. Re-uploading updates existing assets by IP address.
                    </p>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                    <Input
                        label="Asset inventory CSV"
                        type="file"
                        accept=".csv,text/csv"
                        disabled={busy}
                        className="flex-1"
                        onChange={(event) => {
                            setFile(event.target.files?.[0] || null);
                            setError('');
                            setDiagnostics([]);
                            setResult(null);
                            setImportingFile('');
                        }}
                    />
                    <Button icon={CloudUploadOutlined} loading={busy} disabled={!file} onClick={upload}>
                        Import inventory
                    </Button>
                </div>

                {busy && (
                    <Alert variant="info" title="Import in progress">
                        Importing <span className="font-medium">{importingFile}</span>. Keep this page open.
                    </Alert>
                )}

                {error && (
                    <Alert variant="error" title="Inventory import failed">
                        <p>{error}</p>
                        {diagnostics.length > 0 && (
                            <details className="mt-2">
                                <summary className="cursor-pointer font-medium">
                                    View {diagnostics.length} row-level error{diagnostics.length === 1 ? '' : 's'}
                                </summary>
                                <ul className="mt-2 max-h-60 space-y-1 overflow-y-auto pl-5">
                                    {diagnostics.map((diagnostic) => (
                                        <li key={`${diagnostic.row}-${diagnostic.reason}`}>
                                            CSV row {diagnostic.row}: {diagnostic.reason}
                                        </li>
                                    ))}
                                </ul>
                            </details>
                        )}
                    </Alert>
                )}
                {result && (
                    <Alert variant="success" title="Inventory imported">
                        <p>
                            <span className="font-medium">{file?.name}</span> —{' '}
                            <span className="tabular">{result.imported}</span> FM030 assets processed:
                            {' '}<span className="tabular">{result.created}</span> created and{' '}
                            <span className="tabular">{result.updated}</span> updated.
                            {' '}{result.summary.ignoredOtherProjects} records from other projects and{' '}
                            {result.summary.emptyRows} empty rows were ignored.
                        </p>
                        <div className="mt-3 grid gap-3 sm:grid-cols-2">
                            <div>
                                <p className="font-medium">Imported by building</p>
                                <ul className="mt-1">
                                    {Object.entries(result.summary.byBuilding).map(([building, count]) => (
                                        <li key={building}>{building}: {count}</li>
                                    ))}
                                </ul>
                            </div>
                            <div>
                                <p className="font-medium">Imported by device type</p>
                                <ul className="mt-1">
                                    {Object.entries(result.summary.byDeviceType).map(([type, count]) => (
                                        <li key={type}>{type}: {count}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                        <details className="mt-3">
                            <summary className="cursor-pointer font-medium">
                                View uploaded assets and locations ({importedAssets.length})
                            </summary>
                            {importedAssets.length > 0 ? (
                                <div className="mt-2 max-h-80 overflow-auto rounded border border-border">
                                    <table className="w-full min-w-[700px] text-left text-xs">
                                        <thead className="sticky top-0 bg-surface">
                                            <tr>
                                                <th className="px-2 py-2">Asset</th>
                                                <th className="px-2 py-2">Type</th>
                                                <th className="px-2 py-2">IP</th>
                                                <th className="px-2 py-2">Building / floor</th>
                                                <th className="px-2 py-2">Location</th>
                                                <th className="px-2 py-2">Controller</th>
                                                <th className="px-2 py-2">Result</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {importedAssets.map((asset) => (
                                                <tr key={`${asset.ipAddress}-${asset.location}`} className="border-t border-border">
                                                    <td className="px-2 py-1.5">{asset.hostname || '—'}</td>
                                                    <td className="px-2 py-1.5">{asset.deviceType}</td>
                                                    <td className="px-2 py-1.5">{asset.ipAddress}</td>
                                                    <td className="px-2 py-1.5">{asset.building}, {asset.floor}</td>
                                                    <td className="px-2 py-1.5">{asset.location || '—'}</td>
                                                    <td className="px-2 py-1.5">{asset.controller}</td>
                                                    <td className="px-2 py-1.5 capitalize">{asset.action}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <p className="mt-2">
                                    This import response did not include individual asset details. The building and
                                    device-type totals above are available; restart/update the PAS API to see each
                                    asset’s location here.
                                </p>
                            )}
                        </details>
                    </Alert>
                )}
            </div>
        </Card>
    );
};

export default PasInventoryUpload;
